import { useState } from "react";
import axios from "axios";
import { FundingForm } from "../types";
import CompanyDetails from "./CompanyDetails";
import ContactPersonDetails from "./ContactPersonDetails";
import FundingDetails from "./FundingDetails";
import { MdOutlineClose } from "react-icons/md";
import { displaySuccess, displayError, convertToNumber } from "utils";
import HoneypotField from "components/shared/HoneypotField";
import useSpamGuard from "hooks/useSpamGuard";

interface ApplyForFundingFormProps {
  closer: (value: boolean) => void;
}

const initialValue = {
  businessName: "",
  website: "",
  businessEmail: "",
  uploadedFiles: [],
  projectDescription: "",
  country: "",
  state: "",
  currency: "",
  amount: "",
  firstName: "",
  lastName: "",
  email: "",
  role: "",
  phone: "",
  countryCode: "",
  lastUserCountry: "",
};

const ApplyForFundingForm = ({ closer }: ApplyForFundingFormProps) => {
  const [step, setStep] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FundingForm>(initialValue);
  const { honeypotProps, isLikelyBot } = useSpamGuard();

  const handleFieldChange = (field: keyof FundingForm, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = (values: Partial<FundingForm>) => {
    setFormData((prev) => ({ ...prev, ...values }));
    setStep((s) => s + 1);
  };

  const handleBack = () => setStep((s) => s - 1);

  const cleanFormValues = (obj: FundingForm) => {
    const cleaned: Partial<FundingForm> = {};

    Object.entries(obj).forEach(([key, value]) => {
      if (value === "" || value === null || value === undefined) return;

      if (key === "amount") {
        let num = convertToNumber(value as string);
        if (!isNaN(num) && num > 0) {
          cleaned[key as keyof FundingForm] = num as any;
        }
        return;
      }

      if (Array.isArray(value)) {
        if (key === "uploadedFiles") {
          const filtered = value.map((file: any) => ({
            S3Key: file.S3Key,
            Bucket: file.Bucket,
            type: file.type,
          }));
          if (filtered.length > 0)
            cleaned[key as keyof FundingForm] = filtered as any;
        } else if (value.length > 0) {
          cleaned[key as keyof FundingForm] = value as any;
        }
        return;
      }

      if (key === "lastUserCountry") {
        return;
      }

      cleaned[key as keyof FundingForm] = value as any;
    });

    return cleaned;
  };

  const handleFinalSubmit = async (values: Partial<FundingForm>) => {
    const allValues = { ...formData, ...values };

    const cleanedValues = cleanFormValues(allValues);

    // Drop silently and mirror the success path, so a bot gets no signal.
    if (isLikelyBot()) {
      displaySuccess("Application Successful");
      closer(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_IAM_URL}/funding-application/create`,
        cleanedValues
      );
      if (response.status === 200) {
        displaySuccess("Application Successful");
      } else {
        displayError("Error submitting form");
      }
    } catch (error) {
      console.error(error);
      displayError("Error submitting form");
    } finally {
      setLoading(false);
      closer(false);
    }
  };

  return (
    <div className='w-screen h-screen z-[9999] fixed top-0 left-0  bg-black bg-opacity-60 flex flex-col items-center overflow-y-auto'>
      <div className='bg-white rounded-xl p-6 mt-8 w-[90%] lg:mb-10 md:w-3/4 lg:max-w-[500px] flex flex-col lg:justify-center font-satoshi'>
        <HoneypotField {...honeypotProps} />

        <div className='flex items-center justify-between pb-3'>
          <div>
            <p className='text-ashShade-16 text-sm'>Step {step} of 3 </p>
            <div className='flex gap-2 items-center'>
              {Array.from({ length: 3 }).map((_, index) => {
                return (
                  <div
                    className={`${step === index + 1 ? "w-12 h-1 bg-blueShades-7" : "w-1 h-1 bg-[#E5EAF0]"} rounded-lg`}
                    key={index}
                  ></div>
                );
              })}
            </div>
          </div>
          <button type='button' className='' onClick={() => closer(false)}>
            <MdOutlineClose size={24} />
          </button>
        </div>
        {step === 1 && (
          <CompanyDetails
            onNext={handleNext}
            initialData={formData}
            onFieldChange={handleFieldChange}
          />
        )}
        {step === 2 && (
          <FundingDetails
            onNext={handleNext}
            onBack={handleBack}
            initialData={formData}
            onFieldChange={handleFieldChange}
          />
        )}
        {step === 3 && (
          <ContactPersonDetails
            onSubmit={handleFinalSubmit}
            onBack={handleBack}
            loading={loading}
            initialData={formData}
            userCountry={formData.country}
            onFieldChange={handleFieldChange}
          />
        )}
      </div>
    </div>
  );
};
export default ApplyForFundingForm;
